import { DisplayError } from "@/components/Error/Error";
import { Loading } from "@/components/Loading/Loading";
import { useOrg } from "@/hooks/org";
import { OrgGuardRequiredStatus } from "@/typed/OrgGuardRequiredStatus";
import { FC, ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface Props {
  requiredOrgStatus?: OrgGuardRequiredStatus;
  children: ReactNode;
  redirectRoute?: string;
}

/**
 * OrgGuard conditionally renders its children based on the user's organization status.
 *
 * @param requiredOrgStatus - A value from the OrgGuardRequiredStatus enum:
 *        - OrgGuardRequiredStatus.ORG_EXISTS (default): Requires an existing organisation.
 *        - OrgGuardRequiredStatus.NO_ORG: Requires no organisation.
 * @param redirectRoute - Route to navigate to if access is blocked. If not provided, navigates back to the previous page.
 */
export const OrgGuard: FC<Props> = ({ requiredOrgStatus = OrgGuardRequiredStatus.ORG_EXISTS, children, redirectRoute }) => {
  // This hook will only return an error when the request to get organisation data fails.
  // Only the orgId is being used so this can be ignored.
  const { orgId, isOrgIdLoading: isLoading } = useOrg();
  const navigate = useNavigate();

  const hasOrg = orgId !== undefined;
  const isAuthorized =
    (requiredOrgStatus === OrgGuardRequiredStatus.ORG_EXISTS && hasOrg) || (requiredOrgStatus === OrgGuardRequiredStatus.NO_ORG && !hasOrg);

  useEffect(() => {
    if (!isLoading && !isAuthorized) {
      if (redirectRoute) {
        navigate(redirectRoute, { replace: true });
      } else {
        navigate(-1); // Go back to the previous page
      }
    }
  }, [isLoading, isAuthorized, redirectRoute, navigate]);

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthorized) {
    return <DisplayError error={{ error_key: "notAuthorized" }} />;
  }

  return <>{children}</>;
};
